"use client";

import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { INEQUALITY_SOLVER_STRINGS } from "@/components/tools/inequality-solver";
import { t } from "@/lib/i18n/interpolate";
import {
  type Clause,
  checkClause,
  samplePoint,
} from "@/lib/tool-kit/algebra/inequality";
import type { IntervalSet, Relation } from "@/lib/tool-kit/algebra/intervals";
import type { RelationStep } from "@/lib/tool-kit/algebra/relation";
import {
  formatUni,
  substitutionText,
  type Uni,
  variableText,
} from "@/lib/tool-kit/algebra/uni";
import {
  absR,
  formatStyled,
  type NumberStyle,
  type Rational,
  signR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof INEQUALITY_SOLVER_STRINGS;

export interface PartsContext {
  variable: string;
  strings: Strings;
  style: NumberStyle;
  mark: string;
}

const SPACES = /\s+/g;

/** Comparable text: no spaces, real minus signs and one-character relations. */
function squash(text: string): string {
  return text
    .replace(SPACES, "")
    .replace(/-/g, "−")
    .replace(/<=/g, "≤")
    .replace(/>=/g, "≥");
}

/** A typed clause for display: real minus signs and one-character relations. */
function display(text: string): string {
  return text.replace(/-/g, "−").replace(/<=/g, "≤").replace(/>=/g, "≥");
}

export function chainText(
  parts: Uni[],
  rels: Relation[],
  ctx: PartsContext
): string {
  const pieces: string[] = [];
  parts.forEach((part, i) => {
    pieces.push(formatUni(part, ctx.variable, ctx.style, ctx.mark));
    if (i < rels.length) {
      pieces.push(rels[i]);
    }
  });
  return pieces.join(" ");
}

function termLabel(
  amount: Rational,
  degree: number,
  ctx: PartsContext
): string {
  const size = absR(amount);
  const unit = size.num === BigInt(1) && size.den === BigInt(1);
  const body =
    unit && degree > 0 ? "" : formatStyled(size, ctx.style, ctx.mark);
  const wrapped = body.includes("/") && degree > 0 ? `(${body})` : body;
  return `${wrapped}${variableText(ctx.variable, degree)}`;
}

function describe(step: RelationStep, ctx: PartsContext): string {
  const { strings } = ctx;
  const every = step.parts.length === 3 ? strings.allParts : strings.bothSides;
  const amount = step.amount;
  if (!amount) {
    return strings.simplify;
  }
  const show = formatStyled(amount, ctx.style, ctx.mark);
  switch (step.kind) {
    case "clear":
      return t(strings.clear, { factor: show, every });
    case "move-variable":
    case "move-constant":
      return t(signR(amount) > 0 ? strings.subtract : strings.add, {
        term: termLabel(amount, step.degree ?? 0, ctx),
        every,
      });
    case "divide":
      return t(step.flipped ? strings.divideNegative : strings.divide, {
        divisor: show,
        every,
      });
    default:
      return strings.simplify;
  }
}

export function ClauseSteps({
  clause,
  ctx,
}: {
  clause: Clause;
  ctx: PartsContext;
}) {
  const { strings } = ctx;
  const steps = clause.solution.steps;
  const first = chainText(steps[0].parts, steps[0].rels, ctx);
  const rows: WorkedStep[] = [
    {
      text: strings.start,
      math: clause.source ? display(clause.source) : first,
    },
  ];
  steps.forEach((step, index) => {
    const math = chainText(step.parts, step.rels, ctx);
    if (index === 0) {
      if (clause.source !== null && squash(math) === squash(clause.source)) {
        return;
      }
      rows.push({ text: strings.simplify, math });
      return;
    }
    if (step.kind === "reverse") {
      rows.push({ text: strings.reverse, math });
      return;
    }
    const before = steps[index - 1].rels;
    rows.push({
      text: describe(step, ctx),
      math,
      highlight: step.flipped === true,
      note: t(strings.flipNote, {
        from: before.join(", "),
        to: step.rels.join(", "),
      }),
    });
  });
  return <StepList steps={rows} />;
}

export function SampleCheck({
  clause,
  set,
  ctx,
}: {
  clause: Clause;
  set: IntervalSet;
  ctx: PartsContext;
}) {
  const point = samplePoint(set);
  if (!point) {
    return null;
  }
  const { strings } = ctx;
  const check = checkClause(clause, point);
  const show = (value: Rational) => formatStyled(value, ctx.style, ctx.mark);
  const join = (texts: string[]) => {
    const pieces: string[] = [];
    texts.forEach((text, i) => {
      pieces.push(text);
      if (i < clause.rels.length) {
        pieces.push(clause.rels[i]);
      }
    });
    return pieces.join(" ");
  };
  const working = join(
    clause.parts.map((part) =>
      substitutionText(part, point, ctx.style, ctx.mark)
    )
  );
  return (
    <div className="space-y-1 text-sm">
      <p className="text-muted-foreground">
        {t(strings.checkIntro, { variable: ctx.variable, value: show(point) })}
      </p>
      <p className="font-mono text-foreground">{working}</p>
      <p className="font-mono text-foreground">
        {join(check.values.map(show))} —{" "}
        {check.holds ? strings.true : strings.false}
      </p>
    </div>
  );
}
